import { useContext } from 'react';

import type {
  Langs,
  LangCopy,
  TranslationCopy,
  TranslationContent
} from '@CS/components/TranslationProvider/types';

import { Context } from '@CS/components/TranslationProvider';

type Translations = Record<string, string>;

const showTranslationError = (copy: LangCopy, lang: string) => {
  console.error(
    `😱 Translation for "${lang}" language was not provided in`,
    copy,
    '\n',
    'Please, add a proper translation in the content object or provide a fallback language to reuse an existing one.'
  );

  return '';
};

const translateCopy = (copy: TranslationCopy, fallback?: string) => {
  const lang = useContext(Context);
  if (typeof copy === 'string') return copy;

  if (!Object.prototype.hasOwnProperty.call(copy, lang)) {
    const fallbackCopy = (copy as LangCopy)[fallback as Langs];
    return fallbackCopy ?? showTranslationError(copy as LangCopy, lang);
  }

  return (copy as LangCopy)[lang as Langs] ?? '';
};

const translateContent = (
  content: TranslationContent,
  fallback?: string,
  translations: Record<keyof typeof content, string> = {}
) => {
  for (const copy in content) {
    translations[copy] = translateCopy(content[copy], fallback);
  }

  return translations;
};

export default function useTranslation<
  T extends TranslationContent | TranslationCopy
>(x: T): T extends TranslationCopy ? string : Translations;

export default function useTranslation(
  content: TranslationContent | TranslationCopy,
  fallback?: string
): Translations | string {
  return !Object.prototype.hasOwnProperty.call(content, useContext(Context))
    ? translateContent(content as TranslationContent, fallback)
    : translateCopy(content as TranslationCopy, fallback);
}
